import {
  deletePublicProfile,
  fetchPublicProfile,
  selectPublicProfile,
  updatePublicProfile,
} from '@repo/redux/actions/publicProfile';
import { apiRequest } from '@repo/utils/helpers/api';
import { useEffect, useReducer, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AjaxError } from 'rxjs/internal/ajax/errors';

interface PublicProfileState {
  editing: boolean;
  saving: boolean;
  value: string;
  initialValue: string;
  isChanged: boolean;
  errorMsg?: string;
}

type PublicProfileAction = 'set' | 'edit' | 'save' | 'cancel' | 'validationError';

const publicProfileReducer = (
  state: PublicProfileState,
  [type, value]: [PublicProfileAction, any?],
): PublicProfileState => {
  switch (type) {
    case 'set':
      return {
        ...state,
        initialValue: value ?? '',
        value: value ?? '',
        isChanged: false,
        saving: false,
        errorMsg: null,
      };
    case 'edit':
      return { ...state, value, isChanged: value != state.initialValue, errorMsg: null };

    case 'save':
      return { ...state, editing: false, saving: true };

    case 'cancel':
      return {
        ...state,
        value: state.initialValue,
        editing: false,
        errorMsg: null,
        isChanged: false,
      };

    case 'validationError':
      return { ...state, errorMsg: value };

    default:
      return state;
  }
};

interface PublicProfileUrlEditorOpts {
  forceDarkMode?: boolean;
}

interface PublicProfileUrlEditor {
  value: string;
  enabled: boolean;
  saving: boolean;
  isChanged: boolean;
  errorMsg?: string;
  inputRef: React.RefObject<HTMLInputElement>;
  handleInputChange: (value: string) => void;
  handleInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  toggleEnabled: () => void;
  saveChanges: () => void;
}

export const usePublicProfileUrlEditor = (
  opts?: PublicProfileUrlEditorOpts,
): PublicProfileUrlEditor => {
  const dispatch = useDispatch();
  const { slug: publicProfile, enabled } = useSelector(selectPublicProfile);

  const [{ value, saving, isChanged, errorMsg }, dispatchLocal] = useReducer(publicProfileReducer, {
    editing: false,
    saving: false,
    value: publicProfile ?? '',
    initialValue: publicProfile ?? '',
    isChanged: false,
  });

  const inputRef = useRef(null);

  const saveChanges = async () => {
    if (!isChanged) {
      return;
    }
    // check validity of the input
    if (inputRef.current && inputRef.current.validity.typeMismatch) {
      return dispatchLocal(['validationError', 'Please enter a valid handle']);
    }

    if (inputRef.current && inputRef.current.validity.valueMissing) {
      return dispatchLocal(['validationError', 'A valid handle is required']);
    }

    if (!(await validateSlugInput())) {
      return;
    }

    dispatchLocal(['save']);
    dispatch(updatePublicProfile({ slug: value }));
  };

  const handleInputKeyDown = (e) => {
    // "Enter"
    if (e.keyCode == 13) {
      saveChanges();
    }

    // "Esc"
    if (e.keyCode == 27) {
      e.stopPropagation();
      dispatchLocal(['cancel']);
    }
  };

  const handleInputChange = (value: string) => {
    dispatchLocal(['edit', value]);
  };

  async function validateSlugInput(): Promise<boolean> {
    let response;
    try {
      response = await apiRequest<{
        slug: string;
        detail?: string;
        result: 'valid' | 'invalid' | 'taken';
      }>(
        'POST',
        '/profile/validate-slug/',
        { slug: value },
        {
          apiVersion: 'v2',
        },
      );
    } catch (err) {
      if (err instanceof AjaxError && err.status < 500) {
        response = err.response;
      } else {
        dispatchLocal([
          'validationError',
          'There was a problem validating your handle. Please try again in a bit.',
        ]);
        return false;
      }
    }

    if (response.result === 'taken') {
      dispatchLocal(['validationError', 'This handle is already taken']);
    } else if (response.result === 'invalid') {
      dispatchLocal(['validationError', response.detail ?? 'Please enter a valid handle']);
    }

    return response.result === 'valid';
  }

  async function toggleEnabled() {
    if (enabled) {
      window.app.confirm(
        {
          title: `Disable public profile?`,
          message: `Disabling your public profile will free up your unique handle for someone else to use. You can re-enable your public profile at any time.`,
          action: 'Disable',
          variant: 'danger',
          forceDarkMode: opts?.forceDarkMode || false,
        },
        (confirmed) => {
          if (confirmed) {
            dispatch(deletePublicProfile());
          }
        },
      );
      return;
    }

    dispatchLocal(['save']);
    dispatch(updatePublicProfile({ slug: publicProfile }));
  }

  useEffect(() => {
    dispatch(fetchPublicProfile());
  }, []);

  useEffect(() => {
    dispatchLocal(['set', publicProfile]);
  }, [publicProfile, enabled]);

  return {
    value,
    enabled,
    saving,
    isChanged,
    errorMsg,
    inputRef,
    handleInputChange,
    handleInputKeyDown,
    toggleEnabled,
    saveChanges,
  };
};
